import { useEffect, useState } from "react";
import api, { apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Loader2, MonitorSmartphone, Check, X, Ban } from "lucide-react";
import { toast } from "sonner";

const STATUS_STYLE = {
  pending: "bg-amber-50 text-amber-800 border-amber-200",
  approved: "bg-emerald-50 text-emerald-700 border-emerald-200",
  rejected: "bg-rose-50 text-rose-700 border-rose-200",
};
const FILTERS = [["pending", "Pending"], ["approved", "Approved"], ["rejected", "Rejected"], ["", "All"]];
const fmt = (iso) => (iso ? new Date(iso).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" }) : "—");

export default function DeviceApprovals({ onChanged }) {
  const [filter, setFilter] = useState("pending");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = () => {
    setLoading(true);
    api.get("/devices", { params: filter ? { status: filter } : {} })
      .then((r) => setRows(r.data)).catch((e) => toast.error(apiError(e))).finally(() => setLoading(false));
  };
  useEffect(load, [filter]); // eslint-disable-line react-hooks/exhaustive-deps

  const act = async (d, action) => {
    try {
      await api.post(`/devices/${d.id}/${action}`);
      toast.success(action === "approve" ? `Device approved for ${d.user_name}` : `Device blocked for ${d.user_name}`);
      load();
      onChanged?.();
    } catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div className="space-y-6" data-testid="device-approvals">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="text-3xl font-serif font-semibold">Device Approvals</h1>
          <p className="text-sm text-muted-foreground mt-1">Users can only sign in from devices you approve. Blocking a device signs it out immediately.</p>
        </div>
        <div className="flex gap-1 rounded-lg border border-border p-1 bg-card">
          {FILTERS.map(([v, l]) => (
            <button key={l} onClick={() => setFilter(v)} data-testid={`device-filter-${l.toLowerCase()}`}
              className={`px-3 py-1.5 text-sm rounded-md transition-colors ${filter === v ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground"}`}>{l}</button>
          ))}
        </div>
      </div>
      <div className="rounded-xl border border-border bg-card overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>User</TableHead><TableHead>Device</TableHead><TableHead>Requested</TableHead>
              <TableHead>Last used</TableHead><TableHead>Status</TableHead><TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow><TableCell colSpan={6} className="h-32 text-center"><Loader2 className="h-5 w-5 animate-spin inline text-primary" /></TableCell></TableRow>
            ) : rows.length === 0 ? (
              <TableRow><TableCell colSpan={6} className="h-32 text-center text-sm text-muted-foreground" data-testid="devices-empty">No devices here.</TableCell></TableRow>
            ) : rows.map((d) => (
              <TableRow key={d.id} data-testid={`device-row-${d.id}`}>
                <TableCell className="font-medium">{d.user_name}<div className="text-xs text-muted-foreground font-normal">{d.email}</div></TableCell>
                <TableCell className="text-sm"><span className="inline-flex items-center gap-1.5"><MonitorSmartphone className="h-4 w-4 text-primary" />{d.device_name}</span><div className="text-xs text-muted-foreground font-mono">{d.ip || ""}</div></TableCell>
                <TableCell className="text-sm text-muted-foreground">{fmt(d.requested_at)}</TableCell>
                <TableCell className="text-sm text-muted-foreground">{fmt(d.last_used)}</TableCell>
                <TableCell>
                  <span className={`text-xs px-2.5 py-1 rounded-full border font-medium capitalize ${STATUS_STYLE[d.status]}`} data-testid={`device-status-${d.id}`}>{d.status}</span>
                  {d.decided_by && <div className="text-[11px] text-muted-foreground mt-1">by {d.decided_by}</div>}
                </TableCell>
                <TableCell className="text-right">
                  <div className="flex justify-end gap-1">
                    {d.status !== "approved" && (
                      <Button size="sm" className="h-8 bg-emerald-600 hover:bg-emerald-700" onClick={() => act(d, "approve")} data-testid={`approve-device-button-${d.id}`}><Check className="h-4 w-4 mr-1" /> Approve</Button>
                    )}
                    {d.status === "pending" && (
                      <Button size="sm" variant="outline" className="h-8 text-destructive border-destructive/40" onClick={() => act(d, "reject")} data-testid={`reject-device-button-${d.id}`}><X className="h-4 w-4 mr-1" /> Reject</Button>
                    )}
                    {d.status === "approved" && d.device_id !== localStorage.getItem("device_id") && (
                      <Button size="sm" variant="outline" className="h-8 text-destructive border-destructive/40" onClick={() => act(d, "reject")} data-testid={`block-device-button-${d.id}`}><Ban className="h-4 w-4 mr-1" /> Block</Button>
                    )}
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
